import { useEffect, useState, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import useUserStore from '@/user/userStore'
import useAccountStore from '@/account/accountStore'
import { useCurrentAccount } from '@/account/useCurrentAccount'
import { usePrimaryAccount } from '@/account/usePrimaryAccount'
import { useFetchRewards } from '@/user/userQueries'
import { analytics } from '@/common/analytics/analytics'
import { getAppShellVisibility } from './appShellVisibility'
import useCanGoBack from '@/common/hooks/useCanGoBack'
import { AccountType, type User, type Account } from '@/common/types'
import type { AppShellVisibility } from './appShellVisibility'

type Result = {
    user: User | null
    activeAccountId: string | null
    primaryAccount: Account | null
    account: Account | null
    isAccountSwitching: boolean
    accounts: Account[]
    rewardsCount: number
    location: ReturnType<typeof useLocation>
    isAdviserUser: boolean
    showAdviserChrome: boolean
    appShellVisibility: AppShellVisibility
    canGoBack: boolean
    goBackSafely: () => void
    isAdviserMenuOpen: boolean
    setIsAdviserMenuOpen: (open: boolean) => void
    isAccountsLoaded: boolean
    ready: boolean
}

export function useAppContainerLogic(): Result {
    const [isAdviserMenuOpen, setIsAdviserMenuOpen] = useState(false)
    const [rewardsCount, setRewardsCount] = useState<number>(0)

    const { user } = useUserStore()
    const account = useCurrentAccount()
    const primaryAccount = usePrimaryAccount()
    const activeAccountId = useAccountStore((s) => s.activeAccountId)
    const isAccountSwitching = useAccountStore((s) => s.isAccountSwitching)
    const isAccountsLoaded = useAccountStore((s) => s.isAccountsLoaded)
    const accountsById = useAccountStore((s) => s.accountsById)
    const accounts = useMemo(() => Object.values(accountsById), [accountsById])
    const { data: rewards = [] } = useFetchRewards()
    const location = useLocation()
    const { canGoBack, goBackSafely } = useCanGoBack()

    const isAdviserUser = primaryAccount?.accountType === AccountType.ADVISER
    const showAdviserChrome = isAdviserUser

    const appShellVisibility = useMemo(
        () =>
            getAppShellVisibility({
                pathname: location.pathname,
                primaryAccount: primaryAccount ?? null,
                activeAccountId,
            }),
        [location.pathname, primaryAccount, activeAccountId],
    )

    useEffect(() => {
        setRewardsCount(
            Array.isArray(rewards) ? rewards.reduce((count, reward) => count + reward.quantityRemaining, 0) : 0,
        )
    }, [rewards])

    // Determine ready state: user completed onboarding AND accounts loaded
    const ready = useMemo(() => {
        if (!user) return false
        if (isAccountSwitching) return false
        if (!isAccountsLoaded) return false

        // Check all required onboarding fields
        if (!user.salutation) return false
        if (!user.addresses?.residentialAddress) return false
        if (!user.mobile) return false
        if (!user.occupation) return false

        // Account-scoped account.termsAccepted (grace-period protected in accountStore) is the
        // sole source of truth for whether T&C has been accepted.
        if (account && account.termsAccepted === false) return false

        return true
    }, [user, account, isAccountSwitching, isAccountsLoaded])

    useEffect(() => {
        if (!ready) return
        analytics.page()
    }, [location, ready])

    return {
        user,
        activeAccountId,
        primaryAccount,
        account,
        isAccountSwitching,
        accounts,
        rewardsCount,
        location,
        isAdviserUser,
        showAdviserChrome,
        appShellVisibility,
        canGoBack,
        goBackSafely,
        isAdviserMenuOpen,
        setIsAdviserMenuOpen,
        isAccountsLoaded,
        ready,
    }
}
